import { BaseService } from './core/BaseService';
import { apiClient } from './core/ApiClient';

class AuthService extends BaseService {
  constructor() {
    super('/users');
  }

  // Login user
  async login(credentials) {
    if (!credentials?.username || !credentials?.password) {
      throw new Error('Username and password are required');
    }
    
    const response = await apiClient.post('/users/login', credentials);
    
    // Store authentication data
    if (response?.access_token) {
      localStorage.setItem('rdx_auth_token', response.access_token);
      localStorage.setItem('rdx_user', JSON.stringify(response.user));
    }
    
    return response;
  }

  // Logout user
  async logout() {
    try {
      // Attempt to call logout endpoint if available
      await apiClient.post('/users/logout').catch(() => {
        // Ignore errors from logout endpoint
      });
    } finally {
      // Always clear local storage
      this.clearAuthData();
    }
  }

  // Clear authentication data
  clearAuthData() {
    localStorage.removeItem('rdx_auth_token');
    localStorage.removeItem('rdx_user');
  }

  // Get current user profile
  async getProfile() {
    return apiClient.get('/users/me');
  }

  // Update user profile
  async updateProfile(userData) {
    const response = await apiClient.put('/users/me', userData);
    
    // Update stored user data
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...response };
      localStorage.setItem('rdx_user', JSON.stringify(updatedUser));
    }
    
    return response;
  }

  // Change password
  async changePassword(passwordData) {
    const { current_password, new_password, confirm_password } = passwordData;
    
    if (!current_password || !new_password) {
      throw new Error('Current password and new password are required');
    }
    
    if (confirm_password && new_password !== confirm_password) {
      throw new Error('New password and confirmation do not match');
    }
    
    return apiClient.post('/users/me/change-password', {
      current_password,
      new_password
    });
  }

  // Verify token validity
  async verifyToken(token) {
    try {
      const response = await apiClient.get('/users/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return !!response;
    } catch (error) {
      return false;
    }
  }

  // Get stored user data
  getCurrentUser() {
    try {
      const userData = localStorage.getItem('rdx_user');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing stored user data:', error);
      return null;
    }
  }

  // Get stored token
  getToken() {
    return localStorage.getItem('rdx_auth_token');
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getToken();
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  // Check user role
  hasRole(requiredRole) {
    const user = this.getCurrentUser();
    return user?.role === requiredRole;
  }

  // Check if user has any of the specified roles
  hasAnyRole(roles) {
    const user = this.getCurrentUser();
    return roles.includes(user?.role);
  }

  // Forgot password
  async forgotPassword(email) {
    if (!email) {
      throw new Error('Email is required');
    }
    
    return apiClient.post('/users/forgot-password', { email });
  }

  // Reset password with token
  async resetPassword(token, newPassword) {
    if (!token || !newPassword) {
      throw new Error('Reset token and new password are required');
    }
    
    return apiClient.post('/users/reset-password', {
      token,
      new_password: newPassword
    });
  }

  // Refresh token if available
  async refreshToken() {
    try {
      const response = await apiClient.post('/users/refresh-token');
      
      if (response?.access_token) {
        localStorage.setItem('rdx_auth_token', response.access_token);
      }
      
      return response;
    } catch (error) {
      this.clearAuthData();
      throw error;
    }
  }

  // Session management
  async extendSession() {
    return apiClient.post('/users/extend-session');
  }

  // Get user permissions
  async getUserPermissions() {
    const user = this.getCurrentUser();
    if (!user) return [];
    
    return apiClient.get(`/users/${user.id}/permissions`);
  }

  // Check specific permission
  async hasPermission(permission) {
    try {
      const permissions = await this.getUserPermissions();
      return permissions.includes(permission);
    } catch (error) {
      console.error('Error checking permission:', error);
      return false;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export class for testing or custom instances
export { AuthService };
